// AnnouncementForm.js
import { db, storage } from './CannabisRetailerApp';
import { collection, addDoc, Timestamp, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import React, { useEffect, useState } from 'react';

const AnnouncementForm = ({ mode = 'create', existingData = null, onSubmit, onBack }) => {
  const [heading, setHeading]       = useState('');
  const [subheading, setSubheading] = useState('');
  const [imageFile, setImageFile]   = useState(null);
  const [imageUrl, setImageUrl]     = useState(''); // hold existing image if any
  const [bulletPoints, setBulletPoints] = useState(['']);
  const [paragraph, setParagraph]   = useState('');
  const [delaySeconds, setDelaySeconds] = useState(existingData?.delaySeconds || 10);


  // preload when editing
  useEffect(() => {
    if (mode === 'edit' && existingData) {
      setHeading(existingData.heading || '');
      setSubheading(existingData.subheading || '');
      setParagraph(existingData.paragraph || '');
      setBulletPoints(existingData.bulletPoints?.length ? existingData.bulletPoints : ['']);
      setDelaySeconds(existingData.delaySeconds || 10);
      setImageUrl(existingData.imageUrl || '');
    }
  }, [mode, existingData]);

  const handleBulletChange = (i, v) => {
    const next = [...bulletPoints];
    next[i] = v;
    setBulletPoints(next);
  };

  const addBulletPoint = () => setBulletPoints([...bulletPoints, '' ]);

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      let finalImageUrl = imageUrl;

      // Only upload if user selected a new image
      if (imageFile) {
        const imageRef = ref(storage, `announcements/${Date.now()}_${imageFile.name}`);
        const snap = await uploadBytes(imageRef, imageFile);
        finalImageUrl = await getDownloadURL(snap.ref);
      }

      const payload = {
        heading,
        subheading,
        paragraph,
        bulletPoints: bulletPoints.filter(p => p.trim() !== ''),
        imageUrl: finalImageUrl,
        delaySeconds,
      };

      if (mode === 'edit' && existingData?.id) {
        await updateDoc(doc(db, 'Announcements', existingData.id), payload);
      } else {
        await addDoc(collection(db, 'Announcements'), {
          ...payload,
          createdAt: Timestamp.now(),
        });
      }

      if (onSubmit) onSubmit();
      if (onBack) onBack();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save announcement.');
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3 bg-gray-100 p-4 rounded shadow-md">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">
          {mode === 'edit' ? 'Edit Announcement' : 'Create Announcement'}
        </h3>
        {onBack && (
          <button type="button" onClick={onBack} className="text-sm text-gray-600">Back</button>
        )}
      </div>

      <input
        type="text" placeholder="Heading"
        className="w-full p-2 border rounded"
        value={heading} onChange={(e) => setHeading(e.target.value)}
      />

      <input
        type="text" placeholder="Subheading"
        className="w-full p-2 border rounded"
        value={subheading} onChange={(e) => setSubheading(e.target.value)}
      />

      {/* show current image if editing */}
      {mode === 'edit' && imageUrl && (
        <img src={imageUrl} alt="Current" className="max-h-40 my-2 object-contain" />
      )}

      <label className="block mb-2">
        <span className="block font-medium mb-1">Image (optional)</span>
        <input
          type="file" accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-700"
        />
      </label>

      <div>
        <label className="block text-sm font-medium mb-1">Bullet Points</label>
        {bulletPoints.map((point, idx) => (
          <input
            key={idx}
            type="text"
            value={point}
            onChange={(e) => handleBulletChange(idx, e.target.value)}
            placeholder={`Point ${idx + 1}`}
            className="w-full mb-1 p-2 border rounded"
          />
        ))}
        <button type="button" onClick={addBulletPoint} className="text-blue-600 text-sm mt-1">
          + Add Point
        </button>
      </div>

      <textarea
        placeholder="Paragraph"
        className="w-full p-2 border rounded"
        rows={3}
        value={paragraph}
        onChange={(e) => setParagraph(e.target.value)}
      />

      <div className="mb-4">
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Delay Before Showing (seconds)
  </label>
  <input
    type="number"
    value={delaySeconds}
    min="0"
    onChange={(e) => setDelaySeconds(parseInt(e.target.value, 10) || 0)}
    className="border rounded px-2 py-1 w-full"
  />
</div>

      <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
        {mode === 'edit' ? 'Update Announcement' : 'Save Announcement'}
      </button>
    </form>
  );
};

export default AnnouncementForm;
